import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-score',
  templateUrl: './score.component.html',
  styleUrls: ['./score.component.scss'],
})
export class ScoreComponent implements OnInit, OnChanges {
  title = 'score';

  @Input() score = 10;
  @Input() isOnAnalysis!: boolean;
  @Input() isOnAi!: boolean;
  @Input() metadataEnable!: boolean;
  @Input() isOnManipulation!: boolean;
  @Input() checkAI!: boolean;
  @Input() docStatus: string = ' ';
  @Input() sideBarCollapsed: boolean = false;

  // Cached computed properties
  warningColor: string = '';
  indexImage: string = '';
  warningText: string = '';
  displayValue: string = '';
  percentage: number = 0;
  backgroundColor: string = '';

  // Thresholds
  highRiskThreshold: number = 80;
  mediumRiskThreshold: number = 70;

  originalityLevelClean: Record<string, string> = {};

  constructor(private translate: TranslateService, private cdr: ChangeDetectorRef) { }

  private langSub!: Subscription;

  ngOnInit(): void {
    this.computeScoreValues();

    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.computeScoreValues();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recalculate only when relevant inputs change
    if (changes['docStatus'] || changes['score']) {
      this.computeScoreValues();
    }

    if (changes['checkAI']) {
      this.cdr.detectChanges();
      this.computeScoreValues();
    }
  }

  private setWarning(
    color: string,
    image: string,
    keyOn: string,
    keyOff: string,
    aiEnabled: boolean
  ) {
    this.setWarningColor(color);
    this.indexImage = image;
    this.warningText = this.translate.instant(aiEnabled ? keyOn : keyOff);
  }

  private computeScoreValues(): void {
    this.getOriginalityScore();
    this.backgroundColor = this.computeBackgroundColor();
  }

  getOriginalityScore(): number {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const originalityDisplay = user?.Institution?.InstitutionSettings?.[0]?.originalityDisplay;
    this.highRiskThreshold = user?.Institution?.InstitutionSettings?.[0]?.originalityRiskFactorHighRisk || 80;
    this.mediumRiskThreshold = user?.Institution?.InstitutionSettings?.[0]?.originalityRiskFactorMediumRisk || 70;

    if (originalityDisplay === 'None') {
      this.displayValue = 'N/A';
      this.setWarningColor('#949494'); // Gray color for None state
      this.indexImage = './assets/report_v2/not_answered.png'; // Info icon for None state
      this.warningText = this.translate.instant('Originality Index is not available when the originality score display option for the report is set to None.');
      return 4; // Return 4 for None state
    }

    if (this.docStatus) {
      this.percentage = parseFloat(this.docStatus);

      if (originalityDisplay === 'Percentage') {
        const capped = Math.min(this.percentage, 100);
        this.displayValue = capped.toFixed(2) + '%';
      } else {
        if (this.percentage >= this.highRiskThreshold) {
          this.displayValue = this.translate.instant('new_entries.high_risk');
        } else if (this.percentage >= this.mediumRiskThreshold) {
          this.displayValue = this.translate.instant('new_entries.medium_risk');
        } else {
          this.displayValue = this.translate.instant('new_entries.low_risk');
        }
      }

      const aiOrMetadataEnabled = this.checkAI && !this.metadataEnable;

      if (this.percentage >= this.highRiskThreshold) {
        this.setWarning(
          '#A50D0D',
          '../../../../../../assets/report_v2/warning-circled-filled.svg',
          'new_entries.high_risk_description',
          'new_entries.high_risk_description_ai_off',
          aiOrMetadataEnabled
        );
        return 1;
      } else if (this.percentage >= this.mediumRiskThreshold) {
        this.setWarning(
          '#fffb00',
          '../../../../../../assets/report_v2/warning-triangle-fillled.svg',
          'new_entries.medium_risk_description',
          'new_entries.medium_risk_description_ai_off',
          aiOrMetadataEnabled
        );
        return 2;
      } else {
        this.setWarning(
          '#269e44',
          '../../../../../../assets/report_v2/check-circled-filled.svg',
          'new_entries.low_risk_description',
          'new_entries.low_risk_description_ai_off',
          aiOrMetadataEnabled
        );
        return 3;
      }
    } else {
      return 0;
    }
  }

  private setWarningColor(color: string): void {
    this.warningColor = color;
  }

  private computeBackgroundColor(): string {
    if (this.warningColor) {
      const rgba = this.warningColor.match(/[0-9a-fA-F]{2}/g)?.map((hex) => parseInt(hex, 16));
      return `rgba(${rgba?.join(',')}, 0.2)`;
    } else {
      return `rgba(0, 0, 0, 0.2)`;
    }
  }

  openLink(link: string): void {
    window.open(link, '_blank');
  }
}
